import { useEffect, useState } from "react";
import type { PoliticaPrivacidadDTO } from "@ecommerce/http-services";
import { HttpPoliticaPrivacidad } from "../http";

export interface EstadoPoliticaPrivacidad {
    cargando: boolean;
    /** Null mientras carga o si la consulta falló. */
    politica: PoliticaPrivacidadDTO | null;
    error: boolean;
}

// El pie de página y la página de la política comparten la misma consulta: una sola petición por sesión.
let consultaCompartida: Promise<PoliticaPrivacidadDTO> | null = null;

const consultar = (bitForzar: boolean): Promise<PoliticaPrivacidadDTO> => {
    if (bitForzar || !consultaCompartida) {
        consultaCompartida = HttpPoliticaPrivacidad.get().catch((error: unknown) => {
            // Una consulta fallida no se cachea: la próxima vez se vuelve a intentar.
            consultaCompartida = null;
            throw error;
        });
    }
    return consultaCompartida;
};

/** Solo para tests: descarta la consulta compartida. */
export const reiniciarConsultaPoliticaPrivacidad = () => {
    consultaCompartida = null;
};

/**
 * Política de privacidad de la tienda.
 * Con `bitForzar` ignora lo cacheado y pide la versión más reciente (lo usa la página de la política,
 * para no mostrar un PDF viejo si el administrador lo acaba de reemplazar).
 */
export function usePoliticaPrivacidad(bitForzar = false): EstadoPoliticaPrivacidad {
    const [estado, setEstado] = useState<EstadoPoliticaPrivacidad>({
        cargando: true,
        politica: null,
        error: false,
    });

    useEffect(() => {
        let bitCancelado = false;

        consultar(bitForzar)
            .then((politica) => {
                if (!bitCancelado) setEstado({ cargando: false, politica, error: false });
            })
            .catch(() => {
                if (!bitCancelado) setEstado({ cargando: false, politica: null, error: true });
            });

        return () => {
            bitCancelado = true;
        };
    }, [bitForzar]);

    return estado;
}

/** Solo se enlazan URLs http(s): descarta cualquier otro esquema (javascript:, data:...). */
export const urlPoliticaSegura = (vchUrl: string | null | undefined): string | null => {
    if (!vchUrl) return null;
    try {
        const url = new URL(vchUrl);
        return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : null;
    } catch {
        return null;
    }
};
